import {computed, ref} from 'vue';
import {defineStore} from 'pinia';
import {useAuth, useNuxtApp} from '#imports';
import {useErrorHandler} from '@malt/utils';
import type {TJoyIconsNames} from '@maltjoy/core-vue';
import type {ComplianceStepResource, FetchError} from '@api';
import {ComplianceMenuApi} from '@api';
import {apiConfiguration} from '~/configuration/apiConfiguration';
import {RouteNames} from '~/configuration/routeNames';
import {type MenuItemLink, MenuItemStatus} from '~/models/MenuItems.types';

export const useMenuStore = defineStore('menu', () => {
    const {$i18next} = useNuxtApp();
    const {currentIdentityIsSubcontractor} = useAuth();
    const complianceMenuApi = new ComplianceMenuApi(apiConfiguration);
    const {handleFetchError} = useErrorHandler();

    const menuItemList = ref<ComplianceStepResource[]>([]);
    const menuItemLinks = computed<MenuItemLink[]>(() => {
        return menuItemList.value.map((menuItem) => toMenuItemLink(menuItem));
    });

    enum ComplianceEditionMenu {
        LEGAL_REPRESENTATIVE = 'legal-representative',
        BUSINESS_DETAILS = 'business-details',
        COMPANY_LEGAL_DOCUMENTS = 'company-legal-documents',
        BANK_ACCOUNT = 'payment',
        UMBRELLA_FREELANCER_REQUEST = 'umbrella-freelancer-request',
        PAYMENTS_LINK = 'payments-link',
    }

    function toMenuItemLink(complianceStep: ComplianceStepResource): MenuItemLink {
        const complianceEditionMenu = _toComplianceEditionMenu(complianceStep.label);

        return {
            icon: _toMenuItemIcons(complianceEditionMenu!),
            name: _toMenuItemLabel(complianceEditionMenu!) ?? '',
            routeName: _toRouteName(complianceEditionMenu!) ?? '',
            status: _toMenuItemStatus(complianceStep.state),
        };
    }

    function _toComplianceEditionMenu(complianceMenuResourceLabel: string): ComplianceEditionMenu | null {
        switch (complianceMenuResourceLabel) {
            case 'LEGAL_REPRESENTATIVE':
                return ComplianceEditionMenu.LEGAL_REPRESENTATIVE;
            case 'BUSINESS_DETAILS':
                return ComplianceEditionMenu.BUSINESS_DETAILS;
            case 'COMPANY_LEGAL_DOCUMENTS':
                return ComplianceEditionMenu.COMPANY_LEGAL_DOCUMENTS;
            case 'BANK_ACCOUNT':
                return ComplianceEditionMenu.BANK_ACCOUNT;
            case 'CARRIED_FREELANCER_REQUEST':
                return ComplianceEditionMenu.UMBRELLA_FREELANCER_REQUEST;
            case 'INVOICES_AND_PAYMENTS':
                return ComplianceEditionMenu.PAYMENTS_LINK;
            default:
                return null;
        }
    }

    function _toMenuItemStatus(complianceMenuResourceState: string): MenuItemStatus | undefined {
        switch (complianceMenuResourceState) {
            case 'LOCKED':
                return MenuItemStatus.LOCKED;
            case 'WAITING':
                return MenuItemStatus.WAITING;
            case 'VALID':
                return MenuItemStatus.VALID;
            case 'INVALID':
                return MenuItemStatus.INVALID;
            default:
                return undefined;
        }
    }

    function _toMenuItemLabel(menu: ComplianceEditionMenu): string {
        switch (menu) {
            case ComplianceEditionMenu.LEGAL_REPRESENTATIVE:
                return currentIdentityIsSubcontractor
                    ? $i18next.t('company.compliance.personal.details.menu.title.agency')
                    : $i18next.t('company.compliance.personal.details.menu.title');
            case ComplianceEditionMenu.BUSINESS_DETAILS:
                return $i18next.t('compliance.business.panel.title');
            case ComplianceEditionMenu.COMPANY_LEGAL_DOCUMENTS:
                return $i18next.t('company.compliance.legal.documents.section.title');
            case ComplianceEditionMenu.BANK_ACCOUNT:
                return $i18next.t('compliance.payment.panel.menu.entry.bank.account');
            case ComplianceEditionMenu.UMBRELLA_FREELANCER_REQUEST:
                return $i18next.t('compliance.umbrella.request.panel.title');
            case ComplianceEditionMenu.PAYMENTS_LINK:
                return $i18next.t('compliance.payment.panel.menu.entry.payments');
        }
    }

    function _toMenuItemIcons(menu: ComplianceEditionMenu): TJoyIconsNames | null {
        switch (menu) {
            case ComplianceEditionMenu.LEGAL_REPRESENTATIVE:
                return 'freelancer-placeholder';
            case ComplianceEditionMenu.BUSINESS_DETAILS:
                return 'company-placeholder';
            case ComplianceEditionMenu.COMPANY_LEGAL_DOCUMENTS:
                return 'file-check';
            case ComplianceEditionMenu.BANK_ACCOUNT:
                return 'euro-circle';
            case ComplianceEditionMenu.UMBRELLA_FREELANCER_REQUEST:
                return 'projectmanager-agile';
            case ComplianceEditionMenu.PAYMENTS_LINK:
                return 'file-check';
            default:
                return null;
        }
    }

    function _toRouteName(menu: ComplianceEditionMenu): RouteNames {
        switch (menu) {
            case ComplianceEditionMenu.LEGAL_REPRESENTATIVE:
                return RouteNames.PERSONAL_DETAILS;
            case ComplianceEditionMenu.BUSINESS_DETAILS:
                return RouteNames.BUSINESS_DETAILS;
            case ComplianceEditionMenu.COMPANY_LEGAL_DOCUMENTS:
                return RouteNames.LEGAL_DOCUMENTS;
            case ComplianceEditionMenu.BANK_ACCOUNT:
                return RouteNames.BANK_ACCOUNT;
            case ComplianceEditionMenu.UMBRELLA_FREELANCER_REQUEST:
                return RouteNames.UMBRELLA_FREELANCER_REQUEST;
            case ComplianceEditionMenu.PAYMENTS_LINK:
                return RouteNames.PAYMENTS_LINK;
        }
    }

    async function updateMenu(companyId: string): Promise<void> {
        try {
            const request = companyId ? {companyId} : {};
            const data = await complianceMenuApi.getMenu(request);

            menuItemList.value = data?.menu ?? [];
        } catch (e) {
            const error = e as FetchError;
            if (error.statusCode === 403 || error.statusCode === 401) {
                throw error;
            }
            handleFetchError(error);
        }
    }

    return {
        menuItemList,
        menuItemLinks,
        updateMenu,
    };
});
